import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import ShipmentPanel from './ShipmentPanel';
import AttachmentsPanel from './AttachmentsPanel';

const fmtDate = d => d ? format(new Date(d), 'dd/MM/yyyy') : '—';

const STEPS = ['Order','Indent','BL Entry','Tracking','Payment','Doc Courier','Reconciliation','Rail Out','Transport','Receipt','Complaint','Voucher'];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [auditLog, setAuditLog] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [highlightRemark, setHighlightRemark] = useState(null);

  // Build a per-field flag map: "scope|recordId|field" -> [remark_no, ...]
  const flagMap = (() => {
    const m = {};
    for (const r of remarks) {
      const arr = Array.isArray(r.fields) ? r.fields : [];
      for (const f of arr) {
        const key = `${f.scope}|${f.record_id}|${f.field}`;
        if (!m[key]) m[key] = [];
        m[key].push(r.remark_no);
      }
    }
    return m;
  })();
  const flagsFor = (scope, id, field) => flagMap[`${scope}|${id}|${field}`] || [];

  const goToRemark = (remarkNo) => {
    setTab('remarks');
    setHighlightRemark(remarkNo);
    // Scroll into view after tab renders
    setTimeout(() => {
      const el = document.getElementById(`remark-${remarkNo}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const FlagBadge = ({ scope, id, field }) => {
    const nums = flagsFor(scope, id, field);
    if (nums.length === 0) return null;
    return (
      <span style={{display:'inline-flex',gap:'2px',marginLeft:'6px'}}>
        {nums.map(n => (
          <button
            key={n}
            type="button"
            onClick={(e) => { e.stopPropagation(); goToRemark(n); }}
            title={`Changed — see Remark R${n}`}
            style={{background:'#fee2e2',color:'#b91c1c',border:'1px solid #fecaca',borderRadius:'4px',padding:'1px 5px',fontSize:'10px',fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:'2px'}}
          >⚑ R{n}</button>
        ))}
      </span>
    );
  };

  const loadOrder = useCallback(() => {
    // Don't flip the page into the full "Loading..." state on refreshes —
    // that unmounts the tab content (e.g. ShipmentPanel) and loses the
    // opened BL after a save. Initial load still shows the spinner via the
    // `loading` state's initial value.
    api.get(`/orders/${id}`).then(r => { setOrder(r.data); setLoading(false); }).catch(() => { toast.error('Order not found'); navigate('/orders'); });
  }, [id, navigate]);

  useEffect(() => { loadOrder(); }, [loadOrder]);
  useEffect(() => { api.get(`/remarks/order/${id}`).then(r => setRemarks(r.data)).catch(() => setRemarks([])); }, [id]);

  const loadAudit = () => {
    api.get(`/audit/order/${id}`).then(r => setAuditLog(r.data));
  };

  const loadRemarks = () => {
    api.get(`/remarks/order/${id}`).then(r => setRemarks(r.data)).catch(() => setRemarks([]));
  };

  const cancelOrder = async () => {
    const reason = window.prompt('Reason for cancelling this order:');
    if (reason == null) return;
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (!window.confirm(`Cancel order ${order.order_no}? This cannot be undone from the UI.`)) return;
    try {
      await api.patch(`/orders/${id}/cancel`, { reason: reason.trim() });
      toast.success('Order cancelled');
      loadOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    }
  };

  if (loading) return <div className="loading">Loading order...</div>;
  if (!order) return null;

  const step = order.workflow_step || 1;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Order: {order.order_no}</h2>
          <p>{order.shipper_name} • {order.quality} • {order.origin_country} • {fmtDate(order.date_of_order)}</p>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline btn-sm" onClick={() => navigate(`/orders/${id}/edit`)}>Edit Order</button>
          {order.overall_status !== 'Cancelled' && (
            <button className="btn btn-outline btn-sm" style={{color:'var(--accent)',borderColor:'var(--accent)'}} onClick={cancelOrder}>Cancel Order</button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>← Back</button>
        </div>
      </div>

      {/* Stepper */}
      <div className="stepper mb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div className="step">
              <div className={`step-dot ${i+1 < step ? 'done' : i+1 === step ? 'active' : ''}`}>{i+1<step ? '✓' : i+1}</div>
              <span className="step-label">{s}</span>
            </div>
            {i < STEPS.length-1 && <div className={`step-line ${i+1 < step ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[['overview','Overview'],['shipments','BL / Shipments'],['attachments','Documents'],['remarks','Remarks'],['audit','Audit Trail']].map(([k,l]) => (
          <div key={k} className={`tab${tab===k?' active':''}`} onClick={() => { setTab(k); if(k==='audit') loadAudit(); if(k==='remarks') loadRemarks(); }}>{l}</div>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid-2" style={{gap:'16px'}}>
          <div className="card">
            <div className="section-title">Order Details</div>
            {[
              ['Order No', order.order_no, 'order_no'],
              ['Date', fmtDate(order.date_of_order), 'date_of_order'],
              ['Indent No', order.indent_no||'—', 'indent_no'],
              ['Shipper', order.shipper_name||'—', 'shipper_name'],
              ['Contact', order.contact_person||'—', 'contact_person'],
              ['Quality', order.quality||'—', 'quality'],
              ['Origin', order.origin_country||'—', 'origin_country'],
              ['Shipping Lines', (order.shipping_lines?.length ? order.shipping_lines.join(', ') : order.shipping_line) || '—', 'shipping_lines'],
              ['ICD', (order.icd_details?.length ? order.icd_details.join(', ') : order.icd_detail) || '—', 'icd_details'],
              ['Contract Wt', order.contract_weight_mt?`${order.contract_weight_mt} MT`:'—', 'contract_weight_mt'],
              ['Rate', order.rate_usd?`$${order.rate_usd}/MT`:'—', 'rate_usd'],
              ['Variation', order.weight_variation!=null && order.weight_variation!=='' ? `${order.weight_variation} MT` : '—', 'weight_variation'],
              ['Status', order.display_status || order.overall_status, null],
              ['Sub Status', order.sub_status || '—', null],
              ['Remarks', order.remarks||'—', 'remarks'],
            ].map(([l,v,k]) => (
              <div key={l} style={{display:'flex',marginBottom:'8px',gap:'12px',alignItems:'center'}}>
                <span style={{minWidth:'120px',color:'var(--text-muted)',fontSize:'12px',fontWeight:600}}>{l}</span>
                <span>{v}</span>
                {k && <FlagBadge scope="order" id={order.id} field={k} />}
              </div>
            ))}
          </div>
          <div>
            <div className="card mb-2">
              <div className="section-title">BL Summary</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',textAlign:'center'}}>
                <div><div style={{fontSize:'22px',fontWeight:700,color:'var(--primary)'}}>{order.shipments?.length||0}</div><div style={{fontSize:'12px',color:'var(--text-muted)'}}>Total BLs</div></div>
                <div><div style={{fontSize:'22px',fontWeight:700}}>{order.shipments?.reduce((s,b)=>s+parseFloat(b.bill_weight_mt||0),0).toFixed(3)||'—'}</div><div style={{fontSize:'12px',color:'var(--text-muted)'}}>Total Bill Wt (MT)</div></div>
                <div><div style={{fontSize:'22px',fontWeight:700}}>{order.shipments?.reduce((s,b)=>s+(parseInt(b.no_of_containers)||0),0)||0}</div><div style={{fontSize:'12px',color:'var(--text-muted)'}}>Total Containers</div></div>
              </div>
            </div>
            {order.complaints?.length > 0 && (
              <div className="card">
                <div className="section-title">Complaints ({order.complaints.length})</div>
                {order.complaints.map(c=>(
                  <div key={c.id} className="alert alert-warning" style={{marginBottom:'6px'}}>
                    <b>{c.complaint_type}</b> — {c.description} <span className={`badge ${c.status==='Open'?'badge-red':c.status==='Resolved'?'badge-green':'badge-yellow'}`}>{c.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'shipments' && <ShipmentPanel order={order} onRefresh={loadOrder} flagsFor={flagsFor} onFlagClick={goToRemark} />}
      {tab === 'attachments' && <AttachmentsPanel orderId={id} />}
      {tab === 'remarks' && (
        <div className="card">
          <div className="section-title">Remarks</div>
          {remarks.length === 0 ? <div className="empty-state">No remarks logged. Remarks are added automatically when fields change after the Indent stage.</div> :
            <div className="table-wrap">
              <table>
                <thead><tr><th style={{width:'70px'}}>R#</th><th>Note</th><th>Changes</th><th style={{width:'160px'}}>By</th><th style={{width:'150px'}}>Date</th></tr></thead>
                <tbody>
                  {remarks.map(r => (
                    <tr key={r.id} id={`remark-${r.remark_no}`} style={highlightRemark === r.remark_no ? {background:'#fef3c7',transition:'background 1s'} : undefined}>
                      <td className="fw-bold text-primary">R{r.remark_no}</td>
                      <td>{r.note}</td>
                      <td style={{fontSize:'12px',color:'var(--text-muted)'}}>
                        {(Array.isArray(r.fields) ? r.fields : []).map((f,i) => (
                          <div key={i}>
                            <b>{f.scope === 'order' ? 'Order' : 'BL'}</b> · {f.field}: <span style={{color:'#b91c1c'}}>{String(f.old_value ?? '—')}</span> → <span style={{color:'#15803d'}}>{String(f.new_value ?? '—')}</span>
                          </div>
                        ))}
                      </td>
                      <td>{r.created_by_name || '—'}</td>
                      <td className="text-muted">{format(new Date(r.created_at),'dd/MM/yyyy HH:mm')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </div>
      )}
      {tab === 'audit' && (
        <div className="card">
          <div className="section-title">Audit Trail</div>
          {auditLog.length === 0 ? <div className="empty-state">No audit records</div> :
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date/Time</th><th>User</th><th>Action</th><th>Field</th><th>Old Value</th><th>New Value</th></tr></thead>
                <tbody>
                  {auditLog.map(a=>(
                    <tr key={a.id}>
                      <td className="text-muted">{format(new Date(a.created_at),'dd/MM/yyyy HH:mm')}</td>
                      <td>{a.full_name}</td>
                      <td><span className="badge badge-blue">{a.action_type}</span></td>
                      <td className="text-muted">{a.field_name||'—'}</td>
                      <td className="text-danger" style={{maxWidth:'150px',overflow:'hidden',textOverflow:'ellipsis'}}>{a.old_value||'—'}</td>
                      <td className="text-success" style={{maxWidth:'150px',overflow:'hidden',textOverflow:'ellipsis'}}>{a.new_value||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </div>
      )}
    </div>
  );
}
